import { useState, useEffect } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  getEntityRelations,
} from '@backstage/plugin-catalog-react';
import { identityApiRef } from '@backstage/core-plugin-api';
import {
  Entity,
  stringifyEntityRef,
  parseEntityRef,
  RELATION_MEMBER_OF,
  UserEntity,
} from '@backstage/catalog-model';
import useAsync from 'react-use/esm/useAsync';

export interface SystemEntity {
  entityRef: string;
  name: string;
  title?: string;
  description?: string;
}

export const useSystemData = () => {
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  const [systems, setSystems] = useState<SystemEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { value: identity } = useAsync(() =>
    identityApi.getBackstageIdentity(),
  );

  useEffect(() => {
    const fetchSystems = async () => {
      if (!identity) {
        setSystems([]);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Parse user reference
        const userRef = parseEntityRef(identity.userEntityRef);

        // Get user entity to find groups
        const user = (await catalogApi.getEntityByRef({
          kind: 'User',
          namespace: userRef.namespace ?? 'default',
          name: userRef.name,
        })) as UserEntity;

        if (user) {
          // Get groups user is member of
          const groups = getEntityRelations(user, RELATION_MEMBER_OF).map(
            entityRef => stringifyEntityRef(entityRef),
          );

          // Fetch System entities owned by user or their groups
          const response = await catalogApi.getEntities({
            filter: {
              kind: 'System',
              'relations.ownedBy': [...groups, `user:${user.metadata.name}`],
            },
          });

          const systemEntities: SystemEntity[] = response.items.map(
            (entity: Entity) => ({
              entityRef: stringifyEntityRef(entity),
              name: entity.metadata.name,
              title: entity.metadata.title,
              description:
                entity.metadata.description || 'No description available',
            }),
          );

          setSystems(systemEntities);
        }
      } catch (err) {
        setError('Failed to fetch systems');
      } finally {
        setLoading(false);
      }
    };

    fetchSystems();
  }, [catalogApi, identity]);

  return { systems, loading, error };
};
